// Vendor Program v2 PR 2 — staff Complaints queue at
// /portfolio-admin/complaints (sidebar "Vendors" group). Borrower
// complaints about vendors land here for triage: acknowledge → resolve, or
// hand off to compliance intake. The server pre-sorts New first; the
// status filter + open drawer are URL-backed (?status= / ?complaint=) so
// the queue is deep-linkable.

import { useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  EmptyState,
  PageHeader,
  Skeleton,
  StatusBadge,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import { useDateFormatter } from "@/utils/datetime";
import { useVendorComplaints } from "@/hooks/portfolioAdmin/useVendorReports";
import type { VendorComplaintStatus } from "@/api/portfolioAdmin/vendorReportsApi";
import { VendorComplaintDrawer } from "@/components/portfolioAdmin/vendors/complaints/VendorComplaintDrawer";
import {
  COMPLAINT_STATUS_LABEL,
  complaintStatusTone,
} from "@/components/portfolioAdmin/vendors/reports/vendorReportFormat";

const STATUS_FILTERS: VendorComplaintStatus[] = [
  "new",
  "acknowledged",
  "resolved",
  "handed-off",
];

function statusFromParams(params: URLSearchParams): VendorComplaintStatus | undefined {
  const raw = params.get("status");
  return STATUS_FILTERS.includes(raw as VendorComplaintStatus)
    ? (raw as VendorComplaintStatus)
    : undefined;
}

export default function VendorComplaintsPage() {
  const { date } = useDateFormatter();
  const [params, setParams] = useSearchParams();
  const statusFilter = statusFromParams(params);
  const openComplaintId = params.get("complaint");

  const complaintsQuery = useVendorComplaints(statusFilter);
  const rows = complaintsQuery.data ?? [];

  const setParam = (key: string, value: string | null) => {
    const updated = new URLSearchParams(params);
    if (value) updated.set(key, value);
    else updated.delete(key);
    setParams(updated, { replace: true });
  };

  return (
    <div className="flex flex-col gap-space-4" data-testid="vendor-complaints-page">
      <PageHeader
        title="Complaints"
        subtitle="Borrower complaints about vendors — acknowledge, resolve, or hand off to compliance."
      />

      <Card>
        <div className="flex items-center justify-between gap-space-3 flex-wrap mb-space-3">
          <h3 className="font-display font-bold text-h3 m-0">Queue</h3>
          <label className="flex items-center gap-space-2 text-caption">
            <span className="text-gray-mid uppercase tracking-label">Status</span>
            <select
              className="h-9 px-space-2 border border-border-light rounded-md text-small bg-white-pure"
              value={statusFilter ?? ""}
              onChange={(e) => setParam("status", e.target.value || null)}
              data-testid="complaints-status-filter"
            >
              <option value="">All</option>
              {STATUS_FILTERS.map((s) => (
                <option key={s} value={s}>{COMPLAINT_STATUS_LABEL[s]}</option>
              ))}
            </select>
          </label>
        </div>

        {complaintsQuery.isLoading && (
          <div className="flex flex-col gap-space-2" data-testid="complaints-loading">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}

        {complaintsQuery.isError && (
          <div data-testid="complaints-error">
            <p className="text-small text-status-error m-0" role="alert">
              Could not load the complaints queue.
            </p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-space-2"
              onClick={() => void complaintsQuery.refetch()}
            >
              Retry
            </Button>
          </div>
        )}

        {complaintsQuery.isSuccess && rows.length === 0 && (
          <div data-testid="complaints-empty">
            <EmptyState
              variant={statusFilter ? "noResults" : "empty"}
              title={
                statusFilter
                  ? `No ${COMPLAINT_STATUS_LABEL[statusFilter].toLowerCase()} complaints`
                  : "No complaints"
              }
              description={
                statusFilter
                  ? "Clear the status filter to see the full queue."
                  : "Borrower complaints filed from the portal will appear here for triage."
              }
            />
          </div>
        )}

        {complaintsQuery.isSuccess && rows.length > 0 && (
          <Table data-testid="complaints-table">
            <THead>
              <TR>
                <TH className="text-white-pure">Received</TH>
                <TH className="text-white-pure">Loan</TH>
                <TH className="text-white-pure">Vendor</TH>
                <TH className="text-white-pure">Complaint</TH>
                <TH className="text-white-pure">Status</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((c) => (
                <TR
                  key={c.id}
                  onClick={() => setParam("complaint", c.id)}
                  className="cursor-pointer"
                  data-testid={`complaint-row-${c.id}`}
                >
                  <TD className="whitespace-nowrap">{date(c.createdAt)}</TD>
                  <TD>{c.loanNumber}</TD>
                  <TD>{c.vendorName}</TD>
                  <TD className="max-w-md">
                    <span className="block truncate">{c.text}</span>
                  </TD>
                  <TD>
                    <StatusBadge tone={complaintStatusTone(c.status)}>
                      {COMPLAINT_STATUS_LABEL[c.status]}
                    </StatusBadge>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      {openComplaintId && (
        <VendorComplaintDrawer
          complaintId={openComplaintId}
          open={!!openComplaintId}
          onClose={() => setParam("complaint", null)}
        />
      )}
    </div>
  );
}
